import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button,
  CircularProgress, Stack, Typography,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { fetchLocationStatusHistory } from '../helpers/locationStatusApi';
import { locationStatusKey } from '../helpers/adminLocationStatus';

function LocationStatusHistoryContent({ accessToken, locationScope, revision }) {
  const [expanded, setExpanded] = useState(false);
  const [entries, setEntries] = useState([]);
  const [nextOffset, setNextOffset] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [failedOffset, setFailedOffset] = useState(0);
  const requestId = useRef(0);
  const accordionId = useId();
  const { country, state, city, churchName } = locationScope;
  const scope = useMemo(() => ({ country, state, city, churchName }), [country, state, city, churchName]);

  const loadPage = useCallback(async (offset = 0) => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError('');
    if (offset === 0) {
      setEntries([]);
      setNextOffset(null);
      setTotal(0);
    }
    try {
      const result = await fetchLocationStatusHistory(scope, accessToken, offset);
      if (currentRequest !== requestId.current) return;
      const page = Array.isArray(result?.history) ? result.history : [];
      setEntries((previous) => {
        const combined = offset === 0 ? page : [...previous, ...page];
        // An event appended between offset pages can repeat a page boundary.
        return [...new Map(combined.map((entry) => [entry.id, entry])).values()];
      });
      setNextOffset(Number.isInteger(result?.nextOffset) ? result.nextOffset : null);
      setTotal(Number(result?.total || 0));
    } catch (requestError) {
      if (currentRequest !== requestId.current) return;
      setFailedOffset(offset);
      setError(requestError.message || 'No se pudo cargar el historial de esta sede.');
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [scope, accessToken]);

  useEffect(() => {
    if (expanded) loadPage(0);
    return () => { requestId.current += 1; };
  }, [expanded, revision, loadPage]);

  return (
    <Accordion expanded={expanded} onChange={(_, nextExpanded) => setExpanded(nextExpanded)} disableGutters
      slotProps={{ transition: { unmountOnExit: true } }} sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: 1, borderColor: 'divider' }}>
      <AccordionSummary expandIcon={<ChevronDown size={18} />} id={`${accordionId}-header`} aria-controls={`${accordionId}-content`}>
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Historial de la sede</Typography>
          <Typography variant="caption" color="text.secondary">Confirmaciones y cambios de estado guardados</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.25}>
          {entries.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary">Mostrando {entries.length} de {total} registros, del más reciente al más antiguo.</Typography>
              <Stack component="ol" spacing={1.25} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {entries.map((entry) => (
                  <Box component="li" key={entry.id} sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.statusLabel}</Typography>
                    <Typography variant="body2" color="text.secondary">{entry.changedAtLabel || 'Fecha no registrada'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Por: {[entry.changedByName, entry.changedByEmail].filter(Boolean).join(' · ') || 'Responsable no registrado'}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )}
          {!loading && !error && entries.length === 0 && <Typography variant="body2" color="text.secondary">No hay confirmaciones ni cambios de estado registrados para esta sede.</Typography>}
          {error && <Alert severity="error" action={<Button color="inherit" onClick={() => loadPage(failedOffset)} disabled={loading}>Reintentar</Button>}>{error}</Alert>}
          {loading && <CircularProgress size={24} aria-label="Cargando historial de la sede" />}
          {nextOffset !== null && !error && <Button onClick={() => loadPage(nextOffset)} disabled={loading}>Ver más</Button>}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function LocationStatusHistory({ accessToken, locationScope, revision = 0 }) {
  // Scope/session changes discard previous entries and collapse synchronously.
  return <LocationStatusHistoryContent key={`${accessToken}:${locationStatusKey(locationScope)}`}
    accessToken={accessToken} locationScope={locationScope} revision={revision} />;
}
